import {
    createSiteBuilderDocumentCodec,
    isSiteBuilderDocument,
    siteBuilderEditorDocumentFromText,
    siteBuilderEmptyEditorDocument,
    siteBuilderPlainTextFromEditorNode,
    SITE_BUILDER_DOCUMENT_KEY,
    type SiteBuilderDocumentContentJson,
    type SiteBuilderDocument,
    type SiteBuilderEditorDocument,
    type SiteBuilderEditorNode,
    type SiteBuilderSectionDocument,
    type SiteBuilderZoneDocument,
} from "./siteBuilderDocument";
import { SHOP_BUILDER_SCHEMA_VERSION } from "./builderSchema";
import { getShopBlockLocalizationFields } from "./localizationSchema";
import type { SiteBuilderBlock, SiteTemplateDesign } from "./types";

export {
    createShopBlockUuid,
    regenerateShopBlockUuids,
    withShopBlockUuids,
} from "./blockIdentity";

export const SHOP_BUILDER_DOCUMENT_KEY = SITE_BUILDER_DOCUMENT_KEY;

export type ShopBuilderContentJson = SiteBuilderDocumentContentJson;
export type ShopBuilderEditorNode = SiteBuilderEditorNode;
export type ShopBuilderEditorDocument = SiteBuilderEditorDocument;
export type ShopBuilderZoneDocument = SiteBuilderZoneDocument;
export type ShopBuilderSectionDocument = SiteBuilderSectionDocument;
export type ShopBuilderDocument = SiteBuilderDocument;

const shopDocumentCodec = createSiteBuilderDocumentCodec(getShopBlockLocalizationFields);

export const shopBuilderEmptyEditorDocument = siteBuilderEmptyEditorDocument;
export const shopBuilderEditorDocumentFromText = siteBuilderEditorDocumentFromText;
export const shopBuilderPlainTextFromEditorNode = siteBuilderPlainTextFromEditorNode;
export const shopBuilderDocumentFromLegacyBlocks = shopDocumentCodec.documentFromLegacyBlocks;
export const isShopBuilderDocument = isSiteBuilderDocument;
export const withShopBuilderDocumentIdentities = shopDocumentCodec.withDocumentIdentities;
export const shopBuilderBlocksFromEditorDocument = shopDocumentCodec.blocksFromEditorDocument;
export const shopBuilderLegacyBlocksFromDocument = shopDocumentCodec.legacyBlocksFromDocument;
export const shopBuilderDocumentFromContentJson = shopDocumentCodec.documentFromContentJson;
export const shopBuilderZoneOverflow = shopDocumentCodec.zoneOverflow;

export function shopBuilderContentJsonFromDocument(
    document: ShopBuilderDocument,
    extras: Record<string, unknown>,
    design?: SiteTemplateDesign | null,
): { blocks: SiteBuilderBlock[]; [key: string]: unknown } {
    const {
        siteBuilderSchemaVersion: _siteBuilderSchemaVersion,
        ...contentJson
    } = shopDocumentCodec.contentJsonFromDocument(document, extras, design);

    return {
        ...contentJson,
        shopBuilderSchemaVersion: SHOP_BUILDER_SCHEMA_VERSION,
    };
}

export type ShopGlobalLayoutPlacement = "prefix" | "suffix";

export function shopGlobalLayoutPlacement(
    block: SiteBuilderBlock,
): ShopGlobalLayoutPlacement | null {
    if (block.type === "promoBar" || block.type === "header") {
        return "prefix";
    }

    if (block.type === "footer") {
        return "suffix";
    }

    if (block.type !== "richContent") {
        return null;
    }

    const region = block.options?.morrowRegion ?? block.options?.lagunaRegion;

    if (region === "promoBar" || region === "header") {
        return "prefix";
    }

    return region === "footer" ? "suffix" : null;
}

export function isShopGlobalLayoutBlock(block: SiteBuilderBlock): boolean {
    return shopGlobalLayoutPlacement(block) !== null;
}

export type ShopBuilderSeparatedContentJson = {
    contentJson: ShopBuilderContentJson;
    globalContentJson: { blocks: SiteBuilderBlock[] };
};

export function splitShopBuilderContentJson(
    contentJson: ShopBuilderContentJson,
): ShopBuilderSeparatedContentJson {
    const blocks = Array.isArray(contentJson.blocks) ? contentJson.blocks : [];
    const globalBlocks = blocks.filter(isShopGlobalLayoutBlock);
    const bodyBlocks = blocks.filter((block) => !isShopGlobalLayoutBlock(block));

    return {
        contentJson: {
            ...contentJson,
            blocks: bodyBlocks,
        },
        globalContentJson: {
            blocks: globalBlocks,
        },
    };
}
